/**
 * Import Job Description matching-loop copy (Figma algo 47631:378821).
 *
 * Live path (Django ER-11619, when the overlay has a File blob):
 *   POST .../job-description/extract/  empty, title_mismatch, matches[]
 *                                      (covering_roles + already_covered)
 *   POST .../job-description/execute/  attach-role | attach-direct |
 *                                      create-new | discard
 *
 * This module still supplies demo copy + timers when Storybook / tests
 * pass a filename-only uploadedFile (no File blob).
 *
 * ER-11670 (Rule 6a) — every bullet assessment message carries a running
 * "(n/total)" counter, and every bullet after the first is preceded by a
 * line reporting how the previous bullet was resolved, a blank line, then
 * "Now the next one." — except on the last bullet, which goes straight to
 * the Rule 9 closing summary. This module owns that copy for the demo /
 * Storybook path only. ER-11669: the live session no longer composes any
 * of this text itself, including the first bullet - the overlay renders
 * the extract response's own `message` (ER-11668) plus each match's own
 * `message` verbatim (see ImportJobDescriptionOverlay.vue's startMatching /
 * applyLiveAction).
 */

export const IMPORT_JD_PHASE = Object.freeze({
  ANALYZING: "analyzing",
  TITLE_MISMATCH: "titleMismatch",
  EMPTY: "empty",
  MATCHING: "matching",
  DONE: "done",
});

export const IMPORT_JD_ACTION = Object.freeze({
  CONTINUE_AS_IS: "continue-as-is",
  ABORT: "abort",
  UPLOAD_NEW_FILE: "upload-new-file",
  ATTACH_ROLE: "attach-role",
  ATTACH_DIRECT: "attach-direct",
  CREATE_NEW: "create-new",
  DISCARD: "discard",
  RETRY: "retry",
});

export const IMPORT_JD_ANALYZE_MS = 2400;

// ER-12076 / ER-12680: max time to wait for a CREATE_NEW responsibility suggestion
// before falling back to the Rule 6c first-clause name + full-bullet context.
export const IMPORT_JD_SUGGEST_TIMEOUT_MS = 20000;

export const IMPORT_JD_FALLBACK_NAME_MAX_CHARS = 60;
export const IMPORT_JD_CONTEXT_MAX_CHARS = 1000;

const FALLBACK_CLAUSE_TERMINATORS = /[.;:\u2014\u2013!?]/;
const FALLBACK_EDGE_CHARS = " \t\n\r.,;:!?-\u2014\u2013\u2026";

function trimEdgeChars(text) {
  let start = 0;
  let end = text.length;
  while (start < end && FALLBACK_EDGE_CHARS.includes(text[start])) start += 1;
  while (end > start && FALLBACK_EDGE_CHARS.includes(text[end - 1])) end -= 1;
  return text.slice(start, end);
}

/**
 * EC Rule 6c fallback (mirrors the IQ microservice's `build_fallback_result`):
 * name = the bullet's first clause, at most 60 chars, cut at a word boundary;
 * context = the full bullet, at most 1000 chars.
 */
export function buildFallbackSuggestion(bullet = "") {
  const collapsed = String(bullet || "")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");
  const clauseEnd = collapsed.search(FALLBACK_CLAUSE_TERMINATORS);
  const clause = clauseEnd > 0 ? collapsed.slice(0, clauseEnd) : collapsed;
  let name = clause;
  if (clause.length > IMPORT_JD_FALLBACK_NAME_MAX_CHARS) {
    name = clause.slice(0, IMPORT_JD_FALLBACK_NAME_MAX_CHARS);
    if (clause[IMPORT_JD_FALLBACK_NAME_MAX_CHARS] !== " ") {
      const lastSpace = name.lastIndexOf(" ");
      if (lastSpace > 0) name = name.slice(0, lastSpace);
    }
  }
  return {
    name: trimEdgeChars(name),
    context: collapsed.slice(0, IMPORT_JD_CONTEXT_MAX_CHARS),
  };
}

export const DEMO_JD_TITLE = "Electrician";

export const DEMO_DOCUMENT = Object.freeze({
  title: "Software Engineer",
  duties: [
    "Design, develop, and maintain software applications and systems",
    "Write clean, efficient, and well-documented code",
    "Participate in code reviews to maintain code quality and share knowledge",
    "Troubleshoot, debug, and resolve software defects",
    "Mentor junior engineers and provide technical guidance",
    "Stay current with emerging technologies and industry best practices",
  ],
});

export const DEMO_BULLETS = Object.freeze([
  {
    index: 1,
    extracted: "Analyzing stakeholder requirements to plan and execute software development",
    matchedResponsibilityId: 1323,
    matchedResponsibilityName: "Design and develop software applications",
    coveringRoles: [{ role_id: 135, role_name: "Software development" }],
  },
  {
    index: 2,
    extracted: "Write clean, efficient, and well-documented code",
    matchedResponsibilityId: 1108,
    matchedResponsibilityName: "Write clean, maintainable code",
    coveringRoles: [],
  },
  {
    index: 3,
    extracted: "Stay current with emerging technologies and industry best practices.",
    matchedResponsibilityId: null,
    matchedResponsibilityName: "",
    coveringRoles: [],
  },
]);

/** Figma Frame 1707478769–8772 — per-bullet match case. */
export const IMPORT_JD_MATCH_CASE = Object.freeze({
  NO_MATCH: "no-match",
  MATCH_UNLINKED: "match-unlinked",
  MATCH_ONE_ROLE: "match-one-role",
  MATCH_MANY_ROLES: "match-many-roles",
});

/** Map one extracted bullet onto the Figma match branches. */
export function matchCaseForBullet(bullet = {}) {
  const matchedId = bullet.matchedResponsibilityId ?? bullet.matched_responsibility_id;
  if (!matchedId) return IMPORT_JD_MATCH_CASE.NO_MATCH;
  const roles = bullet.coveringRoles || bullet.covering_roles || [];
  const n = Array.isArray(roles) ? roles.length : 0;
  if (n === 0) return IMPORT_JD_MATCH_CASE.MATCH_UNLINKED;
  if (n === 1) return IMPORT_JD_MATCH_CASE.MATCH_ONE_ROLE;
  return IMPORT_JD_MATCH_CASE.MATCH_MANY_ROLES;
}

export const DEMO_BULLET_COUNT = 10;

export function resolveDemoOutcome(fileName = "") {
  const name = String(fileName).toLowerCase();
  if (/(empty|none|blank|no[-_ ]?jd|not[-_ ]?a[-_ ]?jd)/.test(name)) {
    return "empty";
  }
  if (/(match|same-title|same_title)/.test(name)) {
    return "match";
  }
  return "mismatch";
}

export function buildTitleMismatchText(positionName, jdTitle = DEMO_JD_TITLE) {
  const position = positionName || "this position";
  return `The file you imported contains job description information but for a job with a different title. You are importing a job description for a ${position} title but the one you imported is for an ${jdTitle}. Do you want to still continue with responsibilities from this file?`;
}

export function buildEmptyDocumentText() {
  return "The file you imported contains no job description information. Upload a file that contains a job description";
}

const STANZA_SEPARATOR = "\n\n";
const MAX_LETTERED_COVERING_ROLES = 3;
const ROLE_LETTERS = "ABC";
const NO_COVERING_ROLE_SENTENCE = "It is not currently attached to any role.";
const ALREADY_COVERED_NOTICE = "Skipped — this responsibility is already covered.";

/**
 * Responsibility "code" for chat copy. Neither the extract nor the execute
 * payload documents a dedicated `code` field today, so this falls back to
 * the id, matching the "#{id}" convention this demo copy already used
 * before ER-11670 (see e.g. the pre-existing "#1323" in the intro line).
 */
function responsibilityCode(bullet = {}) {
  const code = bullet.matchedResponsibilityCode ?? bullet.matched_responsibility_code;
  if (code) return code;
  const id = bullet.matchedResponsibilityId ?? bullet.matched_responsibility_id;
  return id != null ? `#${id}` : "";
}

function roleCode(role = {}) {
  const code = role.role_code ?? role.roleCode;
  if (code) return code;
  const id = role.role_id ?? role.roleId;
  return id != null ? `#${id}` : "";
}

function roleLabel(role = {}) {
  const code = roleCode(role);
  const name = role.role_name ?? role.roleName ?? "";
  if (code && name) return `${code} - ${name}`;
  return code || name;
}

/** Complete Role stanza for every covering-role branch, including zero roles. */
export function attachedRoleSentence(roles = []) {
  const list = Array.isArray(roles) ? roles : [];
  if (!list.length) return NO_COVERING_ROLE_SENTENCE;
  const lettered = list.slice(0, MAX_LETTERED_COVERING_ROLES);
  if (lettered.length === 1) {
    return `It is currently attached to the role ${roleLabel(lettered[0])}.`;
  }
  const refs = lettered.map((role, index) => `${ROLE_LETTERS[index]}) ${roleLabel(role)}`);
  let listing = refs.join(", ");
  const overflow = list.length - lettered.length;
  if (overflow) listing = `${listing}, and ${overflow} more`;
  return `It is currently attached to these roles: ${listing}.`;
}

function alreadyCoveredMessage(bullet = {}) {
  const code = responsibilityCode(bullet);
  const name = bullet.matchedResponsibilityName || bullet.matched_name || "";
  const identity = [code, name].filter(Boolean).join(" - ");
  const attachedDirect = !!(bullet.attached_to_position || bullet.attachedToPosition);
  const roles = bullet.coveringRoles || bullet.covering_roles || [];
  const roleName = roles[0]?.role_name ?? roles[0]?.roleName;
  const detail = attachedDirect
    ? `${identity} is already attached to this position directly.`
    : roleName
      ? `${identity} is already attached to this position via the role ${roleName}.`
      : `${identity} is already attached to this position via an attached Role.`;
  return [ALREADY_COVERED_NOTICE, detail].join(STANZA_SEPARATOR);
}

/**
 * ER-11670 Rule 6a — the counter-prefixed bullet assessment message, by
 * match state. `index`/`total` default to the bullet's own `index` and the
 * demo bullet count so existing callers that only pass a bullet keep working.
 */
export const GENERIC_NO_MATCH_MESSAGE = "No matching responsibility found in the catalog.";

export function buildFirstLiveMatchingText(extractMessage, bullet = {}, total) {
  const count = Number(total) || Number(bullet.index) || 1;
  const raw = String(bullet.message || "").trim();
  const hasMatch =
    bullet.matched === true ||
    !!(bullet.matchedResponsibilityId ?? bullet.matched_responsibility_id);
  const assessment =
    !hasMatch && (!raw || raw === GENERIC_NO_MATCH_MESSAGE)
      ? buildBulletAssessmentMessage(bullet, bullet.index ?? 1, count)
      : raw;
  return [extractMessage, assessment].filter(Boolean).join(STANZA_SEPARATOR);
}

export function buildBulletAssessmentMessage(bullet = {}, index, total) {
  const quote = `"${bullet.extracted || bullet.bullet || ""}"`;
  const n = index ?? bullet.index ?? 1;
  const count = Number(total) || DEMO_BULLET_COUNT;
  const prefix = `(${n}/${count})`;

  if (bullet.already_covered || bullet.alreadyCovered) {
    return alreadyCoveredMessage(bullet);
  }

  if (matchCaseForBullet(bullet) === IMPORT_JD_MATCH_CASE.NO_MATCH) {
    return `${prefix} ${quote} does not match any existing responsibility in the catalog`;
  }

  const matchedName = bullet.matchedResponsibilityName || bullet.matched_name;
  const match = `${responsibilityCode(bullet)} - ${matchedName}`;
  const roles = bullet.coveringRoles || bullet.covering_roles || [];
  return [
    `${prefix} ${quote}`,
    `This looks like the catalog responsibility ${match}.`,
    attachedRoleSentence(roles),
  ].join(STANZA_SEPARATOR);
}

export function buildExtractionIntro(bullet, total = DEMO_BULLET_COUNT) {
  const count = Number(total) || DEMO_BULLET_COUNT;
  const noun = count === 1 ? "responsibility" : "responsibilities";
  // ER-11669: "from the Duties section" was illustrative-only Figma mock
  // copy - extraction has no section-detection capability. Matches the
  // backend's own count-based intro (ER-11668's `extraction_success_message`).
  return `I extracted ${count} ${noun} from this document. Going through them one by one against the Responsibilities catalog.${STANZA_SEPARATOR}${buildBulletAssessmentMessage(
    bullet,
    bullet.index ?? 1,
    count
  )}`;
}

/**
 * ER-11670 Rule 6a — confirmation line reporting how the previous bullet was
 * resolved.
 */
function confirmationLineFor(actionType, bullet, event = {}) {
  switch (actionType) {
    case IMPORT_JD_ACTION.ATTACH_ROLE: {
      const role = (bullet.coveringRoles || bullet.covering_roles || [])[0] || {};
      const roleName = role.role_name ?? role.roleName ?? "";
      return `Role ${roleCode(role)} - ${roleName} was attached to the position.`;
    }
    case IMPORT_JD_ACTION.ATTACH_DIRECT: {
      const name = bullet.matchedResponsibilityName || bullet.matched_name || "";
      return `Responsibility ${responsibilityCode(
        bullet
      )} - ${name} was attached directly to the position.`;
    }
    case IMPORT_JD_ACTION.CREATE_NEW: {
      // A newly-created responsibility has no catalog code yet in the demo
      // flow (the live path's code comes back from the backend create call).
      const name = (event.suggested_name || bullet.extracted || "").trim();
      return `Responsibility "${name}" was created and attached to the position.`;
    }
    case IMPORT_JD_ACTION.DISCARD:
      return "The proposal was discarded.";
    default:
      return "";
  }
}

/**
 * ER-11670 Rule 6a — compose the full text for one dropdown action: the
 * confirmation line, then — unless this was the last bullet — a blank line,
 * "Now the next one.", and the next bullet's counter-prefixed assessment
 * message.
 */
export function composeResultMessage(actionType, bullet, nextBullet, total, event = {}) {
  const line = confirmationLineFor(actionType, bullet, event);

  if (!nextBullet) {
    // Last bullet: no "Now the next one." — Rule 9's closing summary follows
    // as its own message.
    return line;
  }

  const assessment = buildBulletAssessmentMessage(nextBullet, nextBullet.index, total);
  return `${line}${STANZA_SEPARATOR}Now the next one.${STANZA_SEPARATOR}${assessment}`;
}

export function buildAttachRoleResult(bullet, nextBullet, total) {
  return composeResultMessage(IMPORT_JD_ACTION.ATTACH_ROLE, bullet, nextBullet, total);
}

export function buildAttachDirectResult(bullet, nextBullet, total) {
  return composeResultMessage(IMPORT_JD_ACTION.ATTACH_DIRECT, bullet, nextBullet, total);
}

export function buildCreateNewResult(bullet, nextBullet, total, event) {
  return composeResultMessage(IMPORT_JD_ACTION.CREATE_NEW, bullet, nextBullet, total, event);
}

export function buildDiscardResult(bullet, nextBullet, total) {
  return composeResultMessage(IMPORT_JD_ACTION.DISCARD, bullet, nextBullet, total);
}

export function buildDoneText() {
  return "All found responsibilities where processed. You can now close this window.";
}

export function userActionLabel(actionType) {
  switch (actionType) {
    case IMPORT_JD_ACTION.CONTINUE_AS_IS:
      return "Continue As Is";
    case IMPORT_JD_ACTION.ATTACH_ROLE:
      return "Attach role containing matched responsibility";
    case IMPORT_JD_ACTION.ATTACH_DIRECT:
      return "Attach matched responsibility directly to position";
    case IMPORT_JD_ACTION.CREATE_NEW:
      return "Create and attach new responsibility";
    case IMPORT_JD_ACTION.DISCARD:
      return "Discard proposal and move to next";
    case IMPORT_JD_ACTION.UPLOAD_NEW_FILE:
      return "Upload a new file";
    case IMPORT_JD_ACTION.ABORT:
      return "Abort";
    default:
      return "";
  }
}

export function dropdownPropsForBullet(bullet) {
  return {
    matchedResponsibilityId: bullet.matchedResponsibilityId,
    coveringRoles: bullet.coveringRoles || [],
    bulletText: bullet.extracted,
  };
}
